import SearchRenderer from './search-renderer.js';
import { fetchAll, queryWords, runQuery } from './search-core.js';

const CACHE_KEY     = 'jk_search_data_v6'; // v6: + img_blurhash (tint placeholders)
const CACHE_TTL     = 60 * 60 * 1000; // 1 hour

export class Search {
    constructor() {
        this.data     = [];
        this.lastQuery = null;
        this.renderer = new SearchRenderer();

        this.input     = document.getElementById('search-input');
        this.container = document.getElementById('search-results');
        this.debug     = document.getElementById('search-debug');

        if (!this.input || !this.container) return;

        this.init();
    }

    async init() {
        await this.loadData();
        this.bindEvents();
        this.showRandom();
    }

    // ── Data ────────────────────────────────────────────────────────────────

    async loadData() {
        // Check session cache
        try {
            const cached   = sessionStorage.getItem(CACHE_KEY);
            const cacheTime = sessionStorage.getItem(CACHE_KEY + '_time');
            if (cached && cacheTime && (Date.now() - Number(cacheTime) < CACHE_TTL)) {
                this.data = JSON.parse(cached);
                return;
            }
        } catch (_) {}

        // Fetch from PocketBase
        try {
            this.data = await fetchAll(window.JK.pbUrl);

            sessionStorage.setItem(CACHE_KEY, JSON.stringify(this.data));
            sessionStorage.setItem(CACHE_KEY + '_time', Date.now().toString());
        } catch (err) {
            console.error('Search data load failed:', err);
        }
    }

    // ── Events ──────────────────────────────────────────────────────────────

    bindEvents() {
        let debounce;
        this.input.addEventListener('input', e => {
            clearTimeout(debounce);
            debounce = setTimeout(() => this.search(e.target.value.trim()), 150);
        });

        if (this.debug) {
            this.debug.addEventListener('change', () => {
                this.lastResultIds = null; // force re-render
                this.search(this.input.value.trim());
                if (!this.input.value.trim()) this.showRandom(INITIAL_COUNT);
            });
        }

        this.container.addEventListener('click', e => {
            if (e.target.closest('a')) return;
            const card = e.target.closest('[data-wall-link]');
            if (card) window.location.href = card.dataset.wallLink;
        });
    }

    // ── Search ──────────────────────────────────────────────────────────────

    // Matching and ordering live in search-core.js, shared with the relevance
    // harness in scripts/search-eval/ so the two can't drift.
    search(query) {
        if (query === this.lastQuery) return;
        this.lastQuery = query;

        if (!query) {
            this.showRandom();
            return;
        }

        // A query of only too-short words (e.g. "a") leaves the grid as it was
        // rather than rendering an empty state.
        if (!queryWords(query).length) return;

        const results = runQuery(this.data, query);

        // Skip re-render if result set is identical
        const ids = results.map(r => r.id).join(',');
        if (ids === this.lastResultIds) return;
        this.lastResultIds = ids;

        this.renderer.render(results, this.container, this.isDebug());
    }

    showRandom() {
        // The landing grid is the FULL archive, shuffled once per page view
        // (Fisher–Yates) — the renderer's infinite scroll pages through it, and
        // clearing a query restores the same order with no fresh decodes.
        if (!this._randomSet) {
            const a = [...this.data];
            for (let i = a.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [a[i], a[j]] = [a[j], a[i]];
            }
            this._randomSet = a;
        }
        this.renderer.render(this._randomSet, this.container, this.isDebug());
    }

    isDebug() {
        return this.debug?.checked ?? false;
    }
}

export function init() {
    if (document.getElementById('search-input') && !document.getElementById('search-demo-terms')) {
        new Search();
    }
}
